import { HttpClient, HttpContext } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";

import { catchError, from, map, Observable, of, switchMap, take, tap } from "rxjs";

import { sharedEnvironment } from "../../environments/shared-environment";

import { OFFLINE_BYPASS } from "./offline-http.context";
import { OfflineModeStateService } from "./offline-mode-state.service";
import { OfflineModeServerState, OfflineSelection } from "./offline-mode.types";
import { isRouteAllowedOffline, OFFLINE_HOME_ROUTE } from "./offline-policy.util";
import { OfflineStoreService } from "./offline-store.service";
import { OfflineSyncService } from "./offline-sync.service";

interface OfflineModeResponse {
  enabled: boolean;
  idAssignment: string | null;
  updatedAt?: string | null;
}

/**
 * DEV-966 — orquestra ligar/desligar o Modo Offline entre servidor, estado local, snapshot e
 * fila. O servidor é autoritativo: ligar só grava o flag local depois do 2xx do `PUT`, então
 * nunca existe "ligado no cliente, desligado no servidor". Desligar é imediato no local e vai
 * para a fila como item 0 — com rede, drena na mesma hora.
 */
@Injectable({ providedIn: "root" })
export class OfflineModeService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly state = inject(OfflineModeStateService);
  private readonly store = inject(OfflineStoreService);
  private readonly sync = inject(OfflineSyncService);

  private readonly endpoint = `${sharedEnvironment.serviceUrl}/api/content/content-assignment/offline-mode`;
  private readonly bypass = new HttpContext().set(OFFLINE_BYPASS, true);

  public readonly enabled = this.state.enabled;
  public readonly selection = this.state.selection;

  /**
   * Bootstrap: liga os gatilhos da fila e reconcilia com o servidor quando há rede. Uma sessão
   * nova em outro dispositivo assume o estado do servidor; um desligamento feito em outro
   * dispositivo desliga aqui também. Sem rede, o estado local vale até a próxima reconciliação.
   */
  public init(): Observable<void> {
    this.sync.init();

    if (!this.state.isOnline()) return of(undefined);

    return this.http.get<OfflineModeResponse>(this.endpoint, { context: this.bypass }).pipe(
      take(1),
      switchMap((server) => this.reconcile(server)),
      catchError(() => of(undefined))
    );
  }

  /**
   * Liga o modo para a atribuição escolhida. `prefetch` é o carregamento do snapshot (lista,
   * detalhe, participação, status) fornecido pelo app — o interceptor grava tudo que passar.
   */
  public enable(
    selection: OfflineSelection,
    prefetch: () => Observable<unknown> = () => of(undefined)
  ): Observable<void> {
    // Drena a fila antes de trocar de atribuição: registros pendentes da sessão anterior são
    // enviados (o servidor recusaria com 423 depois que outra atribuição virar a escolhida). A fila
    // nunca é apagada aqui — só sai por envio bem-sucedido ou descarte explícito da pessoa.
    return from(this.sync.sync()).pipe(
      switchMap(() =>
        this.http.put<OfflineModeResponse>(
          this.endpoint,
          { enabled: true, idAssignment: selection.idAssignment },
          { context: this.bypass }
        )
      ),
      take(1),
      switchMap(() => from(this.store.clearSnapshotScope("mode"))),
      tap(() => this.state.enable(selection)),
      switchMap(() => prefetch().pipe(catchError(() => of(undefined)))),
      // Ligado de dentro de uma tela permitida (ex.: o próprio conteúdo), fica nela; o guard e
      // os effects das telas cuidam do resto. Caso contrário, vai para Atribuições.
      tap(() => {
        if (!isRouteAllowedOffline(this.router.url))
          void this.router.navigateByUrl(OFFLINE_HOME_ROUTE);
      }),
      map(() => undefined)
    );
  }

  /** Desliga na hora no cliente; o servidor é avisado pela fila (item 0), com ou sem rede agora. */
  public disable(): Observable<void> {
    this.state.disable();

    return from(this.store.clearSnapshotScope("mode")).pipe(
      switchMap(() =>
        this.sync.enqueue({
          kind: "offline-mode",
          method: "PUT",
          url: this.endpoint,
          body: { enabled: false },
          idAssignment: null
        })
      ),
      tap(() => void this.sync.sync()),
      map(() => undefined)
    );
  }

  /**
   * CA 01: o modo só liga pelo alternador deste cliente. O bootstrap nunca religa sozinho a partir do
   * estado do servidor (novo login, outro navegador, logout sem desligar): nesse caso desliga o
   * servidor, que do contrário seguiria recusando com 423 ações de quem não está mais no modo.
   */
  private reconcile(server: OfflineModeServerState): Observable<void> {
    const localEnabled = this.state.enabled();
    const disablePending = this.sync.items().some((item) => item.kind === "offline-mode");

    if (server.enabled) {
      if (!localEnabled) return disablePending ? of(undefined) : this.disable();

      // Ligado aqui e no servidor com outra atribuição (trocada em outro dispositivo): adota a do
      // servidor, que é quem libera o progresso.
      const local = this.state.selection();
      if (server.idAssignment && local?.idAssignment !== server.idAssignment) {
        this.state.enable({
          idAssignment: server.idAssignment,
          idContentTemplate: local?.idContentTemplate ?? ""
        });
      }
      return of(undefined);
    }

    // Servidor desligado. Se há um desligamento pendente na fila, é o nosso — nada a fazer.
    // Se não há, alguém desligou em outro dispositivo: desliga aqui também.
    if (localEnabled && !disablePending) {
      this.state.disable();
      void this.store.clearSnapshotScope("mode");
    }
    return of(undefined);
  }
}
